import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '@/app/store/hooks';
import { logger } from '@/core/logging/logger';
import { selectIsMegaAdmin } from '@/features/authentication';
import { getOrganization } from '@/features/organization-setup';
import { BRANDING_UPDATED_EVENT } from '@/shared/services/organization/loginBrandingStorage';
import { getStoredOrganizationCode } from '@/shared/services/organization/organizationCodeStorage';
import { toMediaUrl } from '@/shared/utils/mediaUrl';
import { DEFAULT_ORG } from '../constants';
import type { SidebarOrg } from '../types';

export const EMPTY_MEGA_ADMIN_ORG: SidebarOrg = {
  id: '',
  name: 'Organization Name',
  logoUrl: undefined,
};

export function useOrganizationBrand(): SidebarOrg {
  const isMegaAdmin = useAppSelector(selectIsMegaAdmin);
  const [org, setOrg] = useState<SidebarOrg>(() =>
    isMegaAdmin ? EMPTY_MEGA_ADMIN_ORG : DEFAULT_ORG,
  );

  const fetchBrand = useCallback(
    async (isMounted: () => boolean = () => true) => {
      if (!isMegaAdmin) return;

      const code = getStoredOrganizationCode();
      if (!code) {
        if (isMounted()) setOrg(EMPTY_MEGA_ADMIN_ORG);
        return;
      }

      try {
        const record = await getOrganization(code, { background: true });
        if (!isMounted()) return;

        const name = record.organizationName?.trim();
        const logoUrl = toMediaUrl(record.orgImage);

        setOrg({
          id: record.organizationCode || '',
          name: name || 'Organization Name',
          logoUrl: logoUrl ?? undefined,
        });
      } catch (error: unknown) {
        logger.debug('Sidebar organization unavailable; showing placeholder brand', error);
        if (isMounted()) setOrg(EMPTY_MEGA_ADMIN_ORG);
      }
    },
    [isMegaAdmin],
  );

  useEffect(() => {
    let live = true;
    void fetchBrand(() => live);

    const handleUpdate = () => {
      void fetchBrand(() => live);
    };

    window.addEventListener(BRANDING_UPDATED_EVENT, handleUpdate);
    return () => {
      live = false;
      window.removeEventListener(BRANDING_UPDATED_EVENT, handleUpdate);
    };
  }, [fetchBrand]);

  return org;
}
